'use client';

import { VoteType } from '@prisma/client';
import { AnimatePresence } from 'framer-motion';
import { LocateFixed, MapPin, Type, X } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import { ReportWithDetails } from '../../../types/shared';
import {
  useTrafficEventsConnection,
  useTrafficGlobalListener,
  type TrafficRealtimeEvent,
} from '../contexts/TrafficGlobalEvents';
import { AddressSearch } from './AddressSearch';
import { ReportJamDialog } from './ReportJamDialog';
import { TrafficCard } from './TrafficCard';
// Импортируем типы для ref карты
import { getUserVotesForReports } from '../lib/actions/trafficActions';
import { TrafficMap, TrafficMapHandle } from './TrafficMap/TrafficMap';
import { TrafficReportModal } from './TrafficReportModal';

type VoteLive = { likes: number; dislikes: number };
type VoteMap = Record<string, VoteLive>;

const FILTERS = [
  { label: 'Все', value: 'ALL' },
  { label: 'Аварии', value: 'ACCIDENT' },
  { label: 'Заторы', value: 'CONGESTION' },
  { label: 'Ремонт', value: 'ROAD_WORKS' },
  { label: 'Другое', value: 'OTHER' },
];

const DISTRICT_OPTIONS = ['Советский', 'Кировский', 'Октябрьский', 'Ленинский', 'Загород'];

// 1. Добавляем список допустимых административных районов Томска
const TOMSK_DISTRICTS = ['Кировский', 'Советский', 'Ленинский', 'Октябрьский'];

const determineDistrict = (text: string | null | undefined): string => {
  if (!text) return 'Загород';

  const matched = DISTRICT_OPTIONS.find((option) =>
    text.toLowerCase().includes(option.toLowerCase()),
  );

  return matched || 'Загород';
};

const findAdministrativeDistrict = (res: any): string => {
  let foundDistrict = 'Загород';

  // geoObjects.each позволяет перебрать все варианты (Микрорайон -> Район -> Город)
  res.geoObjects.each((obj: any) => {
    const name = obj.properties.get('name') || '';

    // Ищем, содержит ли название один из наших районов
    const match = TOMSK_DISTRICTS.find((district) => name.includes(district));

    if (match) {
      foundDistrict = match;
      // false останавливает перебор, так как мы нашли то, что искали
      return false;
    }
  });

  return foundDistrict;
};

export function TrafficClientPage({
  initialReports,
  isAuthenticated,
}: {
  initialReports: ReportWithDetails[];
  isAuthenticated: boolean;
  userId?: string;
}) {
  const { data: session } = useSession();
  const [reports, setReports] = useState<ReportWithDetails[]>(initialReports);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [coordinates, setCoordinates] = useState<number[] | null>(null);
  const [searchMode, setSearchMode] = useState<'none' | 'map' | 'address'>('none');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('ALL');
  const [isMapReady, setIsMapReady] = useState(false);
  const [lastTrafficUpdate, setLastTrafficUpdate] = useState<string | null>(null);
  const [trafficLevel, setTrafficLevel] = useState<number | null>(null);
  const [trafficHint, setTrafficHint] = useState<string | null>(null);
  const [pendingDistrict, setPendingDistrict] = useState<string | null>(null);
  const isSseConnected = useTrafficEventsConnection();
  const [liveVotes, setLiveVotes] = useState<VoteMap>({});
  // ✅ FIX: Правильный тип для Ref
  const mapRef = useRef<TrafficMapHandle>(null);

  // Для SSE
  const selectedIdRef = useRef(selectedId);
  const geocodeTimerRef = useRef<number | null>(null);
  // Обновляем реф с ID при каждом рендере, чтобы SSE видел актуальное значение
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const requestIdleCallback = (
      window as Window & {
        requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    const cancelIdleCallback = (
      window as Window & { cancelIdleCallback?: (handle: number) => void }
    ).cancelIdleCallback;
    if (requestIdleCallback) {
      const id = requestIdleCallback(() => setIsMapReady(true), { timeout: 1500 });
      return () => cancelIdleCallback?.(id);
    }
    const timeout = window.setTimeout(() => setIsMapReady(true), 300);
    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (isAuthenticated && initialReports.length > 0) {
      const reportIds = initialReports.map((r) => r.id);
      getUserVotesForReports(reportIds).then((voteMap) => {
        if (Object.keys(voteMap).length > 0) {
          setReports((prev) =>
            prev.map((r) => {
              if (voteMap[r.id]) {
                return { ...r, myVote: voteMap[r.id] };
              }
              return r;
            }),
          );
        }
      });
    }
  }, [isAuthenticated, initialReports]); // Зависимость от initialReports

  const selectedReport = useMemo(
    () => reports.find((r) => r.id === selectedId),
    [reports, selectedId],
  );

  const handleRealtimeEvent = useCallback((data: TrafficRealtimeEvent) => {
    if (data.type === 'TRAFFIC_LEVEL') {
      const nextLevel =
        data.payload.level === null || data.payload.level === undefined
          ? null
          : Number(data.payload.level);

      setTrafficLevel(Number.isNaN(nextLevel as number) ? null : nextLevel);
      setTrafficHint(data.payload.hint ?? null);
      setLastTrafficUpdate(
        data.payload.time
          ? new Date(data.payload.time).toLocaleTimeString('ru-RU', {
              hour: '2-digit',
              minute: '2-digit',
            })
          : null,
      );
      return;
    }

    // ✅ ВОТ ОН: VOTE_UPDATE не трогаем reports[]
    if (data.type === 'VOTE_UPDATE') {
      setLiveVotes((prev) => ({
        ...prev,
        [data.payload.reportId]: {
          likes: data.payload.likes,
          dislikes: data.payload.dislikes,
        },
      }));
      return;
    }

    setReports((prev) => {
      if (!Array.isArray(prev)) return [];

      switch (data.type) {
        case 'NEW_REPORT':
          if (prev.some((r) => r.id === data.payload.id)) return prev;
          // на всякий случай чистим liveVotes для нового id
          setLiveVotes((lv) => {
            if (!lv[data.payload.id]) return lv;
            const { [data.payload.id]: _, ...rest } = lv;
            return rest;
          });
          return [data.payload, ...prev];

        case 'DELETE_REPORT':
          if (selectedIdRef.current === data.payload.reportId) setSelectedId(null);
          // чистим liveVotes
          setLiveVotes((lv) => {
            if (!lv[data.payload.reportId]) return lv;
            const { [data.payload.reportId]: _, ...rest } = lv;
            return rest;
          });
          return prev.filter((r) => r.id !== data.payload.reportId);

        case 'REPORTS_CLEANED':
          setLiveVotes((lv) => {
            const next = { ...lv };
            data.payload.deletedIds.forEach((id) => delete next[id]);
            return next;
          });
          return prev.filter((r) => !data.payload.deletedIds.includes(r.id));

        case 'NEW_COMMENT':
          return prev.map((r) => {
            if (r.id !== data.payload.reportId) return r;

            const nextComments = [...r.comments, data.payload];
            const prevCount = r._count?.comments ?? r.comments.length;

            return {
              ...r,
              comments: nextComments,
              _count: {
                ...r._count,
                comments: prevCount + 1,
              },
            };
          });

        case 'DELETE_COMMENT':
          return prev.map((r) => {
            if (r.id !== data.payload.reportId) return r;

            const nextComments = r.comments.filter((c) => c.id !== data.payload.commentId);
            const prevCount = r._count?.comments ?? r.comments.length;
            const deletedCount = r.comments.length - nextComments.length;

            return {
              ...r,
              comments: nextComments,
              _count: {
                ...r._count,
                comments: Math.max(0, prevCount - deletedCount),
              },
            };
          });

        default:
          return prev;
      }
    });
  }, []);

  useTrafficGlobalListener(handleRealtimeEvent);

  const handleSelectReport = useCallback((report: ReportWithDetails) => {
    setSelectedId(report.id);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedId(null);
  }, []);

  const handleMapClick = useCallback(
    (coords: number[]) => {
      if (!isAuthenticated) {
        toast.error('Войдите, чтобы добавить событие!');
        return;
      }
      setPendingDistrict(null);

      if (typeof window !== 'undefined' && (window as any).ymaps?.geocode) {
        if (geocodeTimerRef.current) {
          window.clearTimeout(geocodeTimerRef.current);
        }
        geocodeTimerRef.current = window.setTimeout(() => {
          (window as any).ymaps
            .geocode(coords, { kind: 'district' })
            .then((res: any) => {
              const district = findAdministrativeDistrict(res);
              setPendingDistrict(district);
            })
            .catch((err: any) => {
              console.error('Geocode error:', err);
              setPendingDistrict('Загород');
            });
        }, 250);
      }

      setCoordinates(coords);
      setIsDialogOpen(true);
      setSearchMode('none');
    },
    [isAuthenticated],
  );

  const handleGoToMap = useCallback((coords: [number, number]) => {
    mapRef.current?.panTo(coords);
  }, []);

  const locateMe = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          handleGoToMap([latitude, longitude]);
        },
        () => {
          toast.error('Не удалось определить геолокацию');
        },
      );
    }
  }, [handleGoToMap]);

  const handleDeleteReportSuccess = useCallback((reportId: string) => {
    setReports((prev) => prev.filter((r) => r.id !== reportId));
    // Используем функциональное обновление стейта
    setSelectedId((prevId) => (prevId === reportId ? null : prevId));
  }, []);

  const handleAddressSelected = useCallback(
    (coords: [number, number], districtName?: string) => {
      if (!isAuthenticated) {
        toast.error('Войдите, чтобы добавить событие!');
        return;
      }

      // Проверяем, есть ли район от DaData и входит ли он в наш список
      const isValidDaDataDistrict =
        districtName && TOMSK_DISTRICTS.some((d) => districtName.includes(d));

      if (isValidDaDataDistrict && districtName) {
        // Если DaData вернула хороший район (например "Кировский"), берем его
        // Нужно очистить название от лишнего ("Кировский р-н" -> "Кировский")
        const matched = TOMSK_DISTRICTS.find((d) => districtName.includes(d));
        setPendingDistrict(matched || 'Загород');
      } else {
        // Если DaData вернула null (как в твоем логе) или мусор,
        // спрашиваем у Яндекса по координатам, используя ту же умную логику
        if (typeof window !== 'undefined' && (window as any).ymaps?.geocode) {
          if (geocodeTimerRef.current) {
            window.clearTimeout(geocodeTimerRef.current);
          }
          geocodeTimerRef.current = window.setTimeout(() => {
            (window as any).ymaps
              .geocode(coords, { kind: 'district' })
              .then((res: any) => {
                const district = findAdministrativeDistrict(res);
                setPendingDistrict(district);
              })
              .catch(() => setPendingDistrict('Загород'));
          }, 250);
        } else {
          setPendingDistrict('Загород');
        }
      }

      mapRef.current?.panTo(coords);
      setCoordinates(coords);
      setIsDialogOpen(true);
      setSearchMode('none');
    },
    [isAuthenticated],
  );

  const handleVote = useCallback(
    async (reportId: string, voteType: VoteType) => {
      if (!session?.user?.id) {
        toast.error('Войдите, чтобы голосовать');
        return;
      }

      setReports((prev) =>
        prev.map((r) => {
          if (r.id !== reportId) return r;

          const previousVote = r.myVote;
          let newLikes = r.likes;
          let newDislikes = r.dislikes;
          let newVote: VoteType | null = previousVote;

          if (previousVote === voteType) {
            if (voteType === 'LIKE') newLikes = Math.max(0, newLikes - 1);
            else newDislikes = Math.max(0, newDislikes - 1);
            newVote = null;
          } else {
            if (voteType === 'LIKE') {
              newLikes += 1;
              if (previousVote === 'DISLIKE') newDislikes = Math.max(0, newDislikes - 1);
            } else {
              newDislikes += 1;
              if (previousVote === 'LIKE') newLikes = Math.max(0, newLikes - 1);
            }
            newVote = voteType;
          }

          return { ...r, likes: newLikes, dislikes: newDislikes, myVote: newVote };
        }),
      );

      try {
        const res = await fetch('/api/traffic/vote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reportId, voteType }),
        });
        if (!res.ok) throw new Error('Ошибка API');
      } catch {
        toast.error('Не удалось сохранить голос');
        // Тут можно было бы откатить изменения
      }
    },
    [session?.user?.id],
  );
  const getLive = (id: string) => liveVotes[id];

  const filteredReports = useMemo(() => {
    if (activeFilter === 'ALL') return reports;
    return reports.filter((report) => report.type === activeFilter);
  }, [reports, activeFilter]);
  const filteredMapReports =
    selectedDistrict === 'ALL'
      ? filteredReports
      : filteredReports.filter((report) => report.district === selectedDistrict);
  const filteredListReports = filteredMapReports;

  const handleSelectId = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const selectedReportLive = useMemo(() => {
    if (!selectedReport) return null;
    const v = liveVotes[selectedReport.id];
    if (!v) return selectedReport;
    return { ...selectedReport, likes: v.likes, dislikes: v.dislikes };
  }, [selectedReport, liveVotes]);

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6">
        {/* Панель управления (поиск, фильтры) */}
        <div className="p-4 bg-slate-800/50 rounded-lg flex items-center justify-center flex-wrap gap-4">
          {searchMode === 'none' && (
            <>
              <Button onClick={locateMe}>
                <LocateFixed className="mr-2 h-4 w-4" /> Найти меня
              </Button>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setSearchMode('address')}
                    className={cn({ 'cursor-not-allowed': !isAuthenticated })}
                  >
                    <Type className="mr-2 h-4 w-4" /> Ввести адрес
                  </Button>
                </TooltipTrigger>
                {!isAuthenticated && (
                  <TooltipContent className="bg-red-600 text-white border-red-700">
                    Войдите, чтобы сообщать о дорожных событиях.
                  </TooltipContent>
                )}
              </Tooltip>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setSearchMode('map')}
                    className={cn({ 'cursor-not-allowed': !isAuthenticated })}
                  >
                    <MapPin className="mr-2 h-4 w-4" /> Указать на карте
                  </Button>
                </TooltipTrigger>
                {!isAuthenticated && (
                  <TooltipContent className="bg-red-600 text-white border-red-700">
                    Войдите, чтобы сообщать о дорожных событиях.
                  </TooltipContent>
                )}
              </Tooltip>
            </>
          )}
          <span
            className={cn(
              'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold',
              isSseConnected
                ? 'bg-emerald-500/20 text-emerald-200'
                : 'bg-rose-500/20 text-rose-200',
            )}
            aria-live="polite"
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                isSseConnected ? 'bg-emerald-400' : 'bg-rose-400',
              )}
              aria-hidden="true"
            />
            {isSseConnected ? 'Онлайн-обновления активны' : 'Нет соединения, обновления недоступны'}
          </span>
          {lastTrafficUpdate && (
            <span className="text-xs text-slate-200" aria-live="polite">
              Обновлено: {lastTrafficUpdate}
            </span>
          )}
          {trafficLevel !== null && (
            <span className="text-xs text-slate-200" aria-live="polite">
              Пробки: <span className="font-semibold">{trafficLevel}</span>
              {trafficHint ? ` • ${trafficHint}` : ''}
            </span>
          )}
          {searchMode === 'address' && (
            <div className="flex flex-col items-center gap-2 w-full">
              <AddressSearch onAddressSelected={handleAddressSelected} />
              <Button variant="ghost" size="sm" onClick={() => setSearchMode('none')}>
                Отмена
              </Button>
            </div>
          )}
          {searchMode === 'map' && (
            <div className="text-center">
              <p className="text-sky-300 mb-2 animate-pulse">Выберите точку на карте...</p>
              <Button variant="destructive" size="sm" onClick={() => setSearchMode('none')}>
                <X className="mr-2 h-4 w-4" /> Отмена
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Карта */}
          <div className="lg:col-span-3 h-[70vh] rounded-lg overflow-hidden">
            <TrafficMap
              ref={mapRef}
              reports={filteredMapReports}
              onMapClick={handleMapClick}
              isSelectMode={searchMode === 'map'}
              shouldLoad={isMapReady}
            />
          </div>

          {/* Список событий */}
          <div className="lg:col-span-2 max-h-[70vh] flex flex-col">
            <h3 className="text-xl font-bold mb-4 text-main">Что на дорогах?</h3>
            <div className="flex items-center gap-2 mb-4 border-b border-slate-700 pb-2 flex-wrap">
              {FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setActiveFilter(filter.value)}
                  className={cn(
                    'px-3 py-1 text-sm rounded-full transition-colors',
                    activeFilter === filter.value
                      ? 'bg-sky-500 text-white font-semibold'
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700',
                  )}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mb-4 border-b border-slate-700 pb-2 flex-wrap">
              {['ALL', ...DISTRICT_OPTIONS].map((district) => (
                <button
                  key={district}
                  onClick={() => setSelectedDistrict(district)}
                  className={cn(
                    'px-3 py-1 text-sm rounded-full transition-colors',
                    selectedDistrict === district
                      ? 'bg-emerald-500 text-white font-semibold'
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700',
                  )}
                >
                  {district === 'ALL' ? 'Все районы' : district}
                </button>
              ))}
            </div>
            <div className="overflow-y-auto pr-2 flex-1 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800">
              {filteredListReports.length > 0 ? (
                <div className="space-y-4">
                  {filteredListReports.map((report) => {
                    const v = liveVotes[report.id];
                    const likes = v?.likes ?? report.likes;
                    const dislikes = v?.dislikes ?? report.dislikes;

                    return (
                      <TrafficCard
                        key={report.id}
                        report={report}
                        likes={likes}
                        dislikes={dislikes}
                        onGoToMap={handleGoToMap}
                        onDeleteSuccess={handleDeleteReportSuccess}
                        onSelectId={handleSelectId}
                        isSelected={selectedId === report.id}
                        onVote={handleVote}
                      />
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-8">По этому фильтру событий нет.</p>
              )}
            </div>
          </div>
        </div>

        <ReportJamDialog
          isOpen={isDialogOpen}
          setIsOpen={setIsDialogOpen}
          coordinates={coordinates}
          district={pendingDistrict}
          onReported={() => {
            setIsDialogOpen(false);
            toast.success('Сообщение добавлено!');
          }}
        />

        <AnimatePresence>
          {selectedId && selectedReportLive && (
            <TrafficReportModal
              key={selectedReportLive.id}
              report={selectedReportLive}
              onClose={handleCloseModal}
              onVote={handleVote}
            />
          )}
        </AnimatePresence>
      </div>
    </TooltipProvider>
  );
}
